/**
 * @file change-delivery-address.tsx
 * @module app/modals/change-delivery-address
 * @description Modal for selecting or creating a delivery address while editing order delivery details.
 * Supports address list selection, adding a new address, and persisting the selected address.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Card, Col, Form, FormLabel, InputGroup, Row } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CrossSvg from '../../_admin/assets/media/close.png';
import GreenCheck from '../../_admin/assets/media/svg_uTrade/green_check.svg';
import UnCheck from '../../_admin/assets/media/svg_uTrade/unchecked.svg';
import { CustomSelectWhite } from '../custom/Select/CustomSelectWhite';
import { useEffect, useState } from 'react';
import APICallService from '../../api/apiCallService';
import { buyer, customerService, master } from '../../api/apiEndPoints';
import clsx from 'clsx';
import Loader from '../../Global/loader';
import { Add, AddressEditPermission, OrdersDelivery } from '../../utils/constants';
import AutoComplete from '../custom/autoComplete';
import Method from '../../utils/methods';
import { searchGoogleAddressUsingLatLong } from '../../utils/googleAddress';
import { useAuth } from '../modules/auth';
import { useWarehouse } from '../context/WarehouseContext';

const ChangeDeliveryAddress = (props: any) => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedCustomerInfo, setSelectedCustomerInfo] = useState<any>([]);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [address, setAddress] = useState<{
    name: string;
    phoneCountry: string;
    phone: string;
    addressLine1: string;
    landmark: string;
    city: string;
    district: string;
    districtName: string;
    lat: string;
    lng: string;
    ward: string;
  }>({
    name: '',
    phoneCountry: '+255',
    phone: '',
    addressLine1: '',
    landmark: '',
    city: '',
    district: '',
    districtName: '',
    lat: '1',
    lng: '1',
    ward: '',
  });
  const [districts, setDistricts] = useState([]);
  const [wards, setWards] = useState<any[]>([]);
  const [wardLookup, setWardLookup] = useState<Record<string, string>>({});
  const [addAddress, setAddAddress] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [validation, setValidation] = useState<{
    name: boolean;
    phoneCountry: boolean;
    phone: boolean;
    addressLine1: boolean;
    city: boolean;
    district: boolean;
    ward: boolean;
  }>({
    name: false,
    phoneCountry: false,
    phone: false,
    addressLine1: false,
    city: false,
    district: false,
    ward: false,
  });
  const [fetchLoader, setFetchLoader] = useState(false);
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchCustomerInfo(props.details.record.customer.reference);
      await fetchDistrict(page, 10);
      setFetchLoader(false);
    })();
  }, []);

  useEffect(() => {
    if (address?.district) {
      fetchWards(address.district);
    } else {
      setWards([]);
    }
  }, [address?.district]);

  useEffect(() => {
    if (!address?.ward || !wards.length) return;
    const isWardIdSelected = wards.some((ward: any) => ward._id === address.ward);
    if (!isWardIdSelected) {
      const matchedWard = wards.find((ward: any) => ward.name === address.ward);
      if (matchedWard?._id) {
        setAddress((prev: any) => ({
          ...prev,
          ward: matchedWard._id,
        }));
      }
    }
  }, [address?.ward, wards]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const fetchCustomerInfo = async (id: string) => {
    let apiService = new APICallService(
      buyer.details,
      id,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    setSelectedCustomerInfo(response);
    await fetchAddressWardLookup(response?.user?.deliveryAddresses || []);
    const currentAddressId = props?.details?.record?.address?.reference;
    if (response?.user?.deliveryAddresses && currentAddressId) {
      const current = response.user.deliveryAddresses.find(
        (addr: any) => addr._id === currentAddressId
      );
      if (current) {
        setSelectedAddress(current);
      }
    }
  };

  const fetchAddressWardLookup = async (deliveryAddresses: any[] = []) => {
    const districtIds = Array.from(
      new Set(
        deliveryAddresses
          .map((addressItem: any) => {
            if (typeof addressItem?.district === 'string') {
              return addressItem.district;
            }
            return (
              addressItem?.district?.reference || addressItem?.district?._id || ''
            );
          })
          .filter(Boolean)
      )
    );

    if (!districtIds.length) {
      setWardLookup({});
      return;
    }

    const wardApiService = new APICallService(
      master.listWards,
      {
        districts: districtIds,
        needCount: false,
      },
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );

    const wardResponse = await wardApiService.callAPI();
    const lookup = (wardResponse?.records || []).reduce(
      (acc: Record<string, string>, ward: any) => {
        if (ward?._id && ward?.name) {
          acc[ward._id] = ward.name;
        }
        return acc;
      },
      {}
    );

    setWardLookup(lookup);
  };
  const fetchDistrict = async (pageNo: number, limit: number) => {
    let params: any = {};
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value || null;
    }
    let apiService = new APICallService(
      customerService.listDistrict,
      params,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    let data: any = [...districts];
    if (pageNo === 1) {
      setTotalRecords(response.total);
    } else {
      let prevTotal = totalRecords;
      setTotalRecords(prevTotal);
    }
    response.records.forEach((val: any) => {
      data.push({
        value: val._id,
        label: val.name,
        _id: val._id,
        name: val.name,
      });
    });
    setDistricts(data);
  };

  const fetchWards = async (districtId: string) => {
    if (!districtId) {
      setWards([]);
      return [];
    }

    const params: any = {
      districts: [districtId],
      needCount: false,
    };

    const apiService = new APICallService(
      master.listWards,
      params,
      '',
      '',
      '',
      '',
      props.moduleName,
      selectedWarehouse?.value || null
    );

    const response = await apiService.callAPI();
    const records = (response?.records || []).map((ward: any) => ({
      value: ward._id,
      label: ward.name,
      _id: ward._id,
      name: ward.name,
    }));
    setWards(records);
    return records;
  };
  const onMenuScrollToBottom = async () => {
    if (!(districts.length === totalRecords)) {
      let tempPage = page;
      tempPage = tempPage + 1;
      setPage(tempPage);
      await fetchDistrict(tempPage, 10);
    }
  };
  const handleChange = (value: any, name: string) => {
    let temp: any = { ...address };
    let validateTemp: any = { ...validation };
    temp[name] = value.trimStart();
    if (value.trimStart().length > 0) {
      validateTemp[name] = false;
    } else {
      validateTemp[name] = true;
    }
    setAddress(temp);
    setValidation(validateTemp);
  };
  const handleDistrict = async (event: any) => {
    let temp: any = { ...address };
    let tempValidation: any = { ...validation };
    temp.district = event._id;
    temp.districtName = event.label;
    temp.ward = '';
    tempValidation.district = false;
    tempValidation.ward = true;
    setValidation(tempValidation);
    setAddress(temp);
  };

  const handleWard = (event: any) => {
    let temp: any = { ...address };
    let tempValidation: any = { ...validation };
    temp.ward = event?._id || '';
    tempValidation.ward = !(event?._id || '').length;
    setValidation(tempValidation);
    setAddress(temp);
  };
  const handleSaveAddress = async () => {
    let temp = { ...address };
    let validateTemp = { ...validation };
    if (
      !temp.name &&
      !temp.addressLine1 &&
      !temp.city &&
      !temp.district &&
      !temp.ward
    ) {
      validateTemp.name = true;
      validateTemp.phone = true;
      validateTemp.addressLine1 = true;
      validateTemp.city = true;
      validateTemp.district = true;
      validateTemp.ward = true;
    } else if (!temp.name) {
      validateTemp.name = true;
    } else if (!temp.phone) {
      validateTemp.phone = true;
    } else if (!temp.addressLine1) {
      validateTemp.addressLine1 = true;
    } else if (!temp.city) {
      validateTemp.city = true;
    } else if (!temp.district) {
      validateTemp.district = true;
    } else if (!temp.ward) {
      validateTemp.ward = true;
    } else {
      setLoading(true);
      // Use the filter method to find matching objects
      const matchingObjects: any = districts.filter((obj: any) => {
        // we're checking if 'searchText' is present in the 'name' property
        return obj.name.includes(temp.districtName);
      });
      if (matchingObjects.length) {
        temp.districtName = matchingObjects[0].name;
        temp.district = matchingObjects[0]._id;
      } else {
        temp.districtName = 'UpCountry';
        temp.district = '651d5fa8317c6e0dcdcd8fa1';
      }
      let apiService = new APICallService(
        buyer.addAddress,
        temp,
        {
          _id: props.details.record.customer.reference,
        },
        '',
        '',
        '',
        props.moduleName,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();
      if (response) {
        let address: any = { ...selectedCustomerInfo };
        address.user.deliveryAddresses.push(response);
        setSelectedCustomerInfo(address);
        const selectedWard = wards.find(
          (ward: any) => ward._id === response?.ward || ward._id === temp.ward
        );
        if (selectedWard?._id && selectedWard?.name) {
          setWardLookup((prev) => ({
            ...prev,
            [selectedWard._id]: selectedWard.name,
          }));
        }
        setAddress({
          name: '',
          phoneCountry: '+255',
          phone: '',
          addressLine1: '',
          landmark: '',
          city: '',
          district: '',
          districtName: '',
          lat: '1',
          lng: '1',
          ward: '',
        });
        setAddAddress(false);
      }
    }
    setValidation(validateTemp);
    setLoading(false);
  };
  const handleAddressSelect = (address: any) => {
    setSelectedAddress(address);
  };

  const getWardDisplayName = (addressItem: any) => {
    const wardValue = addressItem?.ward;
    if (!wardValue) return '';
    if (typeof wardValue === 'object') {
      return wardValue?.name || wardValue?.label || '';
    }
    return wardLookup[wardValue] || wardValue;
  };

  const handleUpdateSelectedAddress = () => {
    if (selectedAddress) {
      props.onSaveAddress(selectedAddress);
    }
  };

  /**
   * Handles changes to the delivery address input by the user.
   *
   * @param value The address string entered by the user
   * @param lat The latitude of the selected address
   * @param lng The longitude of the selected address
   */
  const handleAddressChange = async (value: string, lat: any, lng: any) => {
    const googleAddress = await searchGoogleAddressUsingLatLong(lat, lng, {
      searchText: value,
      districts,
    });
    const selectedAddress = googleAddress?.data?.[0];
    const normalizedAddress = value.trimStart();
    const districtId = selectedAddress?.district || '651d5fa8317c6e0dcdcd8fa1';
    const wardOptions = await fetchWards(districtId);
    const matchedWard = wardOptions.find(
      (ward: any) => ward.name === selectedAddress?.wardName
    );

    // Update the address state with the new values, ensuring that if certain fields are not available, they are set to default values.
    setAddress((prev: any) => ({
      ...prev,
      ward: matchedWard?._id || '',
      city: selectedAddress?.localityName || '',
      districtName: selectedAddress?.districtName || 'UpCountry',
      district: districtId,
      addressLine1: normalizedAddress,
      lat,
      lng,
    }));

    // Update the validation state based on the presence of required fields. If the selected address does not have a status or if the normalized address is empty, mark those fields as invalid.
    setValidation((prev: any) => ({
      ...prev,
      district: !selectedAddress?.status,
      addressLine1: !normalizedAddress.length,
      ward: !matchedWard?._id,
    }));
  };

  return (
    <>
      <Modal
        show={props.show}
        onHide={props.onHide}
        dialogClassName="modal-dialog-centered min-w-lg-690px"
        className="border-r10px"
        contentClassName="p-5"
      >
        <Modal.Header className="border-bottom-0 text-center pb-6 mx-auto">
          <div className="symbol symbol-md-40px symbol-35px close-inner-top-3">
            <img
              width={40}
              height={40}
              src={CrossSvg}
              alt=""
              onClick={props.onHide}
            />
          </div>
          <Modal.Title className="fs-26 fw-bolder mw-lg-350px">
            Change delivery address
          </Modal.Title>
        </Modal.Header>
        {!fetchLoader ? (
          <>
            <Modal.Body className="pt-0 pb-5">
              <div className="mh-300px scroll-y me-n7 pe-7">
                <Row className="align-items-center g-5">
                  {selectedCustomerInfo &&
                  Object.keys(selectedCustomerInfo).length ? (
                    <>
                      {selectedCustomerInfo.user.deliveryAddresses.map(
                        (val: any) => {
                          const isSelected =
                            (selectedAddress
                              ? selectedAddress._id
                              : props.details.record.address.reference) ===
                            val._id;
                          return (
                            <Col md={12}>
                              <div className="border border-r8px p-6">
                                <Row className="align-items-center">
                                  <Col xs="auto">
                                    <div className="symbol symbol-40px">
                                      <Button className="uw-btn uw-btn-primary"
                                        variant=""
                                        onClick={() => {
                                          handleAddressSelect(val);
                                        }}
                                      >
                                        <img
                                          src={
                                            isSelected ? GreenCheck : UnCheck
                                          }
                                          alt=""
                                        />
                                      </Button>
                                    </div>
                                  </Col>
                                  <Col>
                                    <div className="d-flex flex-column">
                                      <span className="fs-18 fw-600">
                                        {val.name} / {val.phoneCountry}{' '}
                                        {val.phone}
                                      </span>
                                      <span className="fs-16 fw-500">
                                        {[
                                          val.addressLine1,
                                          val.landmark,
                                          val.city,
                                          getWardDisplayName(val),
                                          val.districtName,
                                        ]
                                          .filter((segment: any) =>
                                            String(segment || '').trim().length
                                          )
                                          .join(', ')}
                                      </span>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </Col>
                          );
                        }
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                  {addAddress ? (
                    <Col md={12}>
                      <Card className="bg-f9f9f9">
                        <Card.Body>
                          <Row className="g-7">
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                Shop name
                              </FormLabel>
                              <Form.Control
                                className={clsx(
                                  'form-control-custom bg-white',
                                  validation.name ? 'border-danger' : ''
                                )}
                                value={address.name}
                                onChange={(event: any) => {
                                  handleChange(event.target.value, 'name');
                                }}
                              />
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                Phone number
                              </FormLabel>
                              <InputGroup
                                className={clsx(
                                  validation.phone ? 'border border-danger' : ''
                                )}
                              >
                                <InputGroup.Text className="bg-white border-right-0 fs-16 fw-600 text-black px-6">
                                  +255
                                </InputGroup.Text>
                                <Form.Control
                                  name="phoneNumber"
                                  className="form-control-custom border-active-none bg-white border-left-0 ps-0"
                                  aria-label="Default"
                                  onChange={(event: any) => {
                                    handleChange(event.target.value, 'phone');
                                  }}
                                  value={address.phone}
                                  aria-describedby="inputGroup-sizing-default"
                                />
                              </InputGroup>
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                Address line 1
                              </FormLabel>
                              <AutoComplete
                                address={address.addressLine1}
                                handleAddressChange={handleAddressChange}
                                lat={address.lat}
                                lng={address.lng}
                                border={clsx(
                                  validation.addressLine1 ? '#e55451' : ''
                                )}
                              />
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                Landmark
                              </FormLabel>
                              <Form.Control
                                className={clsx('form-control-custom bg-white')}
                                value={address.landmark}
                                onChange={(event: any) => {
                                  handleChange(event.target.value, 'landmark');
                                }}
                              />
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                City
                              </FormLabel>
                              <Form.Control
                                className={clsx(
                                  'form-control-custom bg-white',
                                  validation.city ? 'border-danger' : ''
                                )}
                                onChange={(event: any) => {
                                  handleChange(event.target.value, 'city');
                                }}
                                value={address.city}
                              />
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                Ward
                              </FormLabel>
                              <CustomSelectWhite
                                border={validation.ward ? '#e55451' : ''}
                                isDisabled={!address.district || loading}
                                default={wards[0] ? wards[0] : []}
                                options={wards}
                                loadingMessage={'Fetching Data'}
                                value={wards.filter((value: any) => {
                                  return (
                                    address.ward === value._id ||
                                    address.ward === value.name
                                  );
                                })}
                                onChange={(e: any) => handleWard(e)}
                              />
                            </Col>
                            <Col md={6}>
                              <FormLabel className="fs-16 fw-500 text-dark">
                                District
                              </FormLabel>
                              <CustomSelectWhite
                                border={validation.district ? '#e55451' : ''}
                                disabled={loading}
                                isLoading={fetchLoader}
                                default={districts[0] ? districts[0] : []}
                                options={districts}
                                loadingMessage={'Fetching Data'}
                                value={districts.filter((value: any) => {
                                  return address.district === value._id;
                                })}
                                onMenuScrollToBottom={onMenuScrollToBottom}
                                onChange={(e: any) => handleDistrict(e)}
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  ) : (
                    <></>
                  )}
                  {!loading ? (
                    <Col md={12} className="justify-content-start">
                      {Method.hasPermission(
                        OrdersDelivery,
                        Add,
                        currentUser
                      ) ? (
                        !addAddress ? (
                          <Button
                            variant="link"
                            className="btn-flush text-primary fs-16 fw-bold"
                            onClick={() => {
                              setAddress({
                                name: '',
                                phoneCountry: '+255',
                                phone: selectedCustomerInfo.user.phone,
                                addressLine1: '',
                                districtName: '',
                                landmark: '',
                                city: '',
                                district: '',
                                lat: '1',
                                lng: '1',
                                ward: '',
                              });
                              setAddAddress(true);
                            }}
                          >
                            + Add new address
                          </Button>
                        ) : (
                          <Button
                            variant="link"
                            className="btn-flush text-danger fs-16 fw-bold"
                            onClick={() => {
                              setAddAddress(false);
                              setAddress({
                                name: '',
                                phoneCountry: '+255',
                                phone: '',
                                addressLine1: '',
                                landmark: '',
                                city: '',
                                district: '',
                                districtName: '',
                                lat: '1',
                                lng: '1',
                                ward: '',
                              });
                              setValidation({
                                name: false,
                                phoneCountry: false,
                                phone: false,
                                addressLine1: false,
                                city: false,
                                district: false,
                                ward: false,
                              });
                            }}
                          >
                            Cancel
                          </Button>
                        )
                      ) : (
                        <></>
                      )}
                    </Col>
                  ) : (
                    <></>
                  )}
                </Row>
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-center pt-0 border-top-0">
              {Method.hasPermission(
                OrdersDelivery,
                AddressEditPermission,
                currentUser
              ) || Method.hasPermission(OrdersDelivery, Add, currentUser) ? (
                <Button
                  variant="primary"
                  className="uw-btn uw-btn-primary min-h-60px"
                  onClick={() => {
                    if (addAddress) {
                      handleSaveAddress();
                    } else {
                      handleUpdateSelectedAddress();
                    }
                  }}
                >
                  {!loading && (
                    <span className="indicator-label">
                      {addAddress ? 'Save address' : 'Update address'}
                    </span>
                  )}
                  {loading && (
                    <span
                      className="indicator-progress"
                      style={{ display: 'block' }}
                    >
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  )}
                </Button>
              ) : (
                <></>
              )}
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Body className="pt-0 pb-5">
              <div className="d-flex justify-content-center align-items-center h-150px">
                <Loader loading={fetchLoader} />
              </div>
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
};
export default ChangeDeliveryAddress;
